import {
  addHours,
  differenceInMonths,
  format,
  formatDistanceStrict,
  intlFormat,
  isAfter,
  isSameDay,
  isValid,
  parseISO,
} from 'date-fns';
import { enUS, es, pt } from 'date-fns/locale';

import { configValue } from '#/config/appConfigUtils';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { IDEALIST_TIME_ZONE } from '#/utils/constants/general/idealistTimeZone';

export const DATE_FNS_LOCALE_BY_LOCALE = {
  [I18nLocale.EN]: enUS,
  [I18nLocale.ES]: es,
  [I18nLocale.PT]: pt,
} satisfies Record<I18nLocale, unknown>;

export function getDate(date: string | number | Date) {
  return typeof date === 'string' ? parseISO(date) : date;
}

function formatTime(
  date: string | number | Date,
  localeCode: string,
  params: Parameters<typeof Intl.DateTimeFormat>[1] = Object.freeze({}),
) {
  const { timeZone = undefined, ...spreadParams } = params;
  return new Date(date).toLocaleTimeString(localeCode, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timeZone ?? undefined,
    ...spreadParams,
  });
}

const SHORTER_DATE_FORMAT = {
  en: 'M/d/yyyy',
  es: 'd/M/yyyy',
  pt: 'd/M/yyyy',
} satisfies Record<I18nLocale, string>;

export function shorterDate({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), SHORTER_DATE_FORMAT[locale]);
}

const SHORTEST_DATE_FORMAT = {
  en: 'M/d',
  es: 'd/M',
  pt: 'd/M',
};

export function shortestDate({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), SHORTEST_DATE_FORMAT[locale]);
}

const SHORTEST_DATE_WITH_YEAR_FORMAT = {
  en: 'M/d/yy',
  es: 'd/M/yy',
  pt: 'd/M/yy',
};

export function shortestDateWithYear({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), SHORTEST_DATE_WITH_YEAR_FORMAT[locale]);
}

export function shorterTime({
  date,
  locale,
  timezone,
  displayTimezone,
}: {
  date: string | number | Date;
  locale: I18nLocale;
  timezone?: string | null;
  displayTimezone?: boolean;
}) {
  const options: Parameters<typeof Intl.DateTimeFormat>[1] = {
    timeZoneName: displayTimezone ? 'short' : undefined,
    timeZone: timezone ?? undefined,
  };
  return formatTime(date, DATE_FNS_LOCALE_BY_LOCALE[locale].code, options);
}

const DATETIME_FORMAT = {
  en: 'MM/dd/yyyy  hh:mma',
  es: 'dd/MM/yyyy HH:mm',
  pt: 'dd/MM/yyyy HH:mm',
};

export function dateTime({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), DATETIME_FORMAT[locale]);
}

const SHORT_DATETIME_FORMAT_BY_LOCALE = {
  en: 'M/d/yy h:mma',
  es: 'd/M/yy HH:mm',
  pt: 'd/M/yy HH:mm',
} satisfies Record<I18nLocale, string>;

export function shortDateTime({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), SHORT_DATETIME_FORMAT_BY_LOCALE[locale]);
}

export function humanDate({
  date,
  locale,
  timezone,
  opts = {},
}: {
  date: string | number | Date;
  locale: I18nLocale;
  timezone?: string | null;
  opts?: {
    shortMonth?: boolean;
  };
}) {
  const parsedDate = getDate(date);
  if (!isValid(parsedDate)) return 'Invalid Date';

  return intlFormat(
    parsedDate,
    {
      year: 'numeric',
      month: opts?.shortMonth ? 'short' : 'long',
      day: 'numeric',
      timeZone: timezone ?? undefined,
    },
    {
      locale: DATE_FNS_LOCALE_BY_LOCALE[locale].code,
    },
  );
}

const HUMAN_DATETIME_FORMAT = {
  en: 'MMM d, yyyy h:mm a',
  es: 'MMM d, yyyy H:mm',
  pt: 'MMM d, yyyy H:mm',
};

export function humanDateTime({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), HUMAN_DATETIME_FORMAT[locale], {
    locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
  });
}

export function monthAndYear({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), 'MMMM yyyy', {
    locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
  });
}

export function dayAndMonth({
  date,
  locale,
  longMonth,
}: {
  date: string | number | Date;
  locale: I18nLocale;
  longMonth?: boolean;
}) {
  return format(getDate(date), longMonth ? 'MMMM d' : 'MMM d', {
    locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
  });
}

export function dowDayAndMonth({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), 'cccc, MMMM d', {
    locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
  });
}

export function dayNumber({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), 'd', {
    locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
  });
}

export function shortMonth({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), 'MMM', {
    locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
  }).slice(0, 3);
}

export function yearOnly({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), 'yyyy', {
    locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
  });
}

export function shortMonthAndYear({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return format(getDate(date), 'MMM yyyy', {
    locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
  });
}

export function now() {
  const dtOverride = configValue('idealist', 'dtOverride');

  if (dtOverride) {
    return parseISO(dtOverride);
  }

  return new Date();
}

export function daysInMs(days: number) {
  return 1000 * 60 * 60 * 24 * days;
}

export function distanceInWordsToNow({
  date,
  locale,
}: {
  date: string | number | Date;
  locale: I18nLocale;
}) {
  return formatDistanceStrict(getDate(date), now(), {
    addSuffix: true,
    locale: DATE_FNS_LOCALE_BY_LOCALE[locale],
    roundingMethod: 'floor',
  });
}

export function distanceInYearsAndMonthsAbbr({
  start,
  end,
}: {
  start: string | number | Date;
  end: string | number | Date;
}) {
  const distance = differenceInMonths(getDate(start), getDate(end));

  if (!distance) {
    return null;
  }

  const years = Math.floor(distance / 12);
  const months = distance - years * 12;
  return [years, months]
    .map((amt, index) =>
      amt === 0
        ? null
        : `${amt} ${index === 0 ? 'yr' : 'mo'}${amt > 1 ? 's' : ''}`,
    )
    .filter((amt) => amt)
    .join(', ');
}

// Converts a date or date string to GMT timezone
export function toGMTDate(date: string | Date) {
  const localDate = new Date(date);
  return new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60000);
}

export function stripTimezone(dateString: string) {
  return dateString.replace('Z', '');
}

// Take a timezone-aware date and return the user's date / time (not timezone-aware)
// as a string
export function formatLocalDate(d: Date): string {
  const convertedDate = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return stripTimezone(convertedDate.toISOString());
}

export function formatIdealistDateTime(d: Date | string): Date {
  // XXX alternative implementation of this function could be this, but would need QA:
  // export const formatIdealistDateTime = (d: Date | string): Date => {
  //   new Date(d.toLocaleString('en-US', { timeZone: IDEALIST_TIME_ZONE }));
  const options = {
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: IDEALIST_TIME_ZONE,
  };
  const formattedDateParts: {
    type: string;
    value: string;
    // @ts-expect-error TS(2345): Argument of type '{ hour12: boolean; year: string;... Remove this comment to see the full error message
  }[] = new Intl.DateTimeFormat('en-us', options).formatToParts(d);
  const dateTimeParams = formattedDateParts
    .filter((datePart) =>
      ['year', 'month', 'day', 'hour', 'minute', 'second'].includes(
        datePart.type,
      ),
    )
    .map((datePart) => parseInt(datePart.value, 10));
  const [month, day, year, hour, minute, second] = dateTimeParams;
  return new Date(year, month - 1, day, hour === 24 ? 0 : hour, minute, second);
}

function getLocalizedToday(date: string | Date) {
  const offsetMatch =
    typeof date === 'string'
      ? /([+-]?[0-9]+)(?::[0-9]+)?$/.exec(date)
      : date.getTimezoneOffset();
  let offsetToDate: number;
  if (typeof offsetMatch === 'number') {
    offsetToDate = offsetMatch;
  } else if (offsetMatch) {
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    offsetToDate = parseInt(offsetMatch.pop(), 10);
  } else {
    offsetToDate = 0;
  }
  const today = now();
  const offsetFromNow = today.getTimezoneOffset() / 60;
  const offsetTotal = offsetToDate + offsetFromNow;
  return addHours(today, offsetTotal);
}

export function dateIsToday(date: string | Date) {
  return isSameDay(getLocalizedToday(date), getDate(date));
}

export function dateIsUpcoming(date: string | Date) {
  const today = getLocalizedToday(date);
  return isAfter(getDate(date), today) || isSameDay(today, getDate(date));
}

export function dateIsPast(date: string | Date) {
  return !dateIsUpcoming(date);
}

export function getCurrentYear() {
  return now().getFullYear();
}
